import XIcon from '../../../../assets/icons/new_icon/x.svg';
import {
  Button,
  DateField,
  ErrorText,
  InputField,
} from '../../../../components';
import SearchDropdown from '../../../../components/global-components/searchDropdown';
import { dateToUtc } from '../../../../functions';
import mapPriorityNameToCode from '../../functions/mapPriorityNameToCode';
import {
  priorityOptions,
  statusOptions,
  travelModeOptions,
} from '../../constants';
import {
  mapStatusNameToCode,
  mapTravelModeNameToCode,
} from '../../functions';
import { useEditJobDetails } from '../../hooks';
import { JobSummary } from '../../interfaces';
import { IconButton } from '../';
import './EditJobDetailsModal.scss';

interface Props {
  editingSummary: JobSummary;
  open: boolean;
  onCloseModal: () => void;
  onModalSave: () => void;
}

export default function EditJobDetailsModal({
  editingSummary,
  open,
  onCloseModal,
  onModalSave,
}: Props) {
  const {
    jobDetails,
    onChangeVendor,
    onSelectPriorityName,
    onSelectStatusName,
    onSelectTravelModeName,
    onChangeDescription,
    onChangeInitialPlannedPickupDate,
    onChangeRevisedPickupDate,
    onChangeActualPickupDate,
    onChangeHandoffLocation,
    onChangeInitialPlannedStagingDate,
    onChangeRevisedStagingDate,
    onClickSave,
    loading,
  } = useEditJobDetails(editingSummary, onModalSave);

  const onClickModalContent = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
  };

  return (
    <div
      className='EditJobDetailsModal'
      hidden={!open}
      onClick={onCloseModal}
    >
      <div
        className='ModalContent'
        onClick={onClickModalContent}
      >
        <div className='ModalHeader'>
          <div className='HeaderText'>
            Job Details
          </div>
          <IconButton
            icon={XIcon}
            onClick={onCloseModal}
          />
        </div>
        <div className='ModalForm'>
          <InputField
            label='Vendor'
            placeholder='Write here'
            value={jobDetails.vendor}
            onChange={onChangeVendor}
          />
          <SearchDropdown
            id='priority-name'
            label='Priority'
            required
            options={priorityOptions}
            selectedValue={mapPriorityNameToCode(jobDetails.priorityName)}
            onSelect={onSelectPriorityName}
          />
          <ErrorText
            text='Please provide a valid priority name.'
            isActive={!jobDetails?.priorityName}
          />
          <SearchDropdown
            id='status'
            label='Status'
            required
            options={statusOptions}
            selectedValue={mapStatusNameToCode(jobDetails.statusName)}
            onSelect={onSelectStatusName}
          />
          <ErrorText
            text='Please provide a valid status name.'
            isActive={!jobDetails?.statusName}
          />
          <SearchDropdown
            id='travel'
            label='Travel'
            required
            options={travelModeOptions}
            selectedValue={mapTravelModeNameToCode(jobDetails.travelModeName)}
            onSelect={onSelectTravelModeName}
          />
          <ErrorText
            text='Please provide a valid travel mode name.'
            isActive={!jobDetails?.travelModeName}
          />
          <InputField
            label='Description'
            placeholder='Write here'
            value={jobDetails.description}
            onChange={onChangeDescription}
          />
          <DateField
            label='Initial Planned Pickup Date'
            value={
              dateToUtc(new Date(jobDetails?.initialPlannedPickupDate))
            }
            isInError={!jobDetails?.initialPlannedPickupDate}
            errorText='Please provide a valid date.'
            required
            onChange={onChangeInitialPlannedPickupDate}
          />
          <DateField
            label='Revised Pickup Date'
            value={new Date(jobDetails?.revisedPickupDate)}
            isInError={!jobDetails?.revisedPickupDate}
            errorText='Please provide a valid date.'
            required
            onChange={onChangeRevisedPickupDate}
          />
          <DateField
            label='Actual Pickup Date'
            value={new Date(jobDetails?.actualPickupDate)}
            isInError={!jobDetails?.actualPickupDate}
            errorText='Please provide a valid date.'
            required
            onChange={onChangeActualPickupDate}
          />
          <InputField
            label='KBX Handoff Location'
            placeholder='Write here'
            value={jobDetails.handoffLocation}
            onChange={onChangeHandoffLocation}
          />
          <DateField
            label='Initial Planned Staging Date'
            value={new Date(jobDetails?.initialPlannedStagingDate)}
            isInError={!jobDetails?.initialPlannedStagingDate}
            errorText='Please provide a valid date.'
            required
            onChange={onChangeInitialPlannedStagingDate}
          />
          <DateField
            label='Revised Staging Date'
            value={new Date(jobDetails?.revisedStagingDate)}
            isInError={!jobDetails?.revisedStagingDate}
            errorText='Please provide a valid date.'
            required
            onChange={onChangeRevisedStagingDate}
          />
          <div className='ActionsContainer'>
            <Button
              text='Save'
              onClick={onClickSave}
              disabled={loading}
            />
            <Button
              text='Cancel'
              onClick={onCloseModal}
              disabled={loading}
              link
            />
          </div>
        </div>
      </div>
    </div>
  );
}
